import { useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useClient } from "ClientContext";
import { useParams } from "react-router-dom";
import { useCheckout } from "context/CheckoutContext";


interface reservationType {
    status: string;
    result: {
        id: number;
        listingMapId: number;
        reservationDate: string;
        guestName: string;
        guestFirstName: string;
        guestLastName: string;
        guestEmail: string;
        numberOfGuests: 2,
        arrivalDate: string;
        departureDate: string;
        checkInTime: number;
        checkOutTime: number;
        nights: number;
        phone: string;
        totalPrice: number;
        remainingBalance: number;
        stripeGuestId: string;
        stripeMessage: string;
        currency: string;
        status: string;
        paymentStatus: string;
    }
}

export function useHostawayReservation() {
    const { selectedAddons, formData } = useCheckout();
    const [reservation, setReservation] = useState<reservationType | null>(null);
    const [reservation_loading, setReservation_loading] = useState(false);
    const [reservation_error, setReservation_error] = useState<string | null>(null);
    const { internal_ID, actions } = useClient();
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const {
        arrival,
        departure,
        listingID,
        currency,
        guests,
        couponCode,
    } = useParams();
    // Memoized fetchReservation function
    const handleFetchReservation = useCallback(
        async (): Promise<void> => {
            setReservation_error(null);
            setReservation_loading(true);
            const speciialRequestAndPolicy = `${formData.card?.specialRequest} Privacy Policy: ${formData.policy.privacy.isAccepted} acceptOn: ${formData.policy.privacy.dateOfAcceptance} | Terms and Conditions: ${formData.policy.termsAndConditions.isAccepted}  acceptOn: ${formData.policy.termsAndConditions.dateOfAcceptance} | Marketing: ${formData.policy.marketing.isAccepted} acceptOn: ${formData.policy.termsAndConditions.dateOfAcceptance} `;
            try {
                const response = await axios.post(`${SERVER_URL}/api/controller`, {
                    action: actions.fetchListingReservation,
                    internal_ID,
                    params: {
                        listingId: listingID,
                        reservation: {
                            guest: {
                                firstName: formData.guest.firstName,
                                lastName: formData.guest.lastName,
                                email: formData.guest.email,
                                phone: formData.guest.phone,
                                specialRequest: speciialRequestAndPolicy || ""
                            },
                            dates: {
                                checkInDateLocalized: arrival,
                                checkOutDateLocalized: departure
                            },
                            cc: {
                                ccNumber: formData.card?.cardNumber,
                                ccName: formData.card?.cardholderName,
                                ccExpirationYear: formData.card?.expirationYear,
                                ccExpirationMonth: formData.card?.expirationMonth,
                                cvc: formData.card?.cvv
                            }
                        },
                        quote: {
                            guestsCount: guests,
                            checkInDateLocalized: arrival,
                            checkOutDateLocalized: departure,
                            currency: currency,
                            coupons: couponCode || null,
                            addons: (selectedAddons.addons || []).map((addon) => ({
                                id: addon.id,
                                title: addon.title,
                                amount: addon.amount,
                                type: addon.type || "",
                                amountType: addon.amountType || ""

                            })),
                        },
                    },
                });

                const reservation = response.data.item;
                setReservation(reservation);
            } catch (err) {
                setReservation_error("We were unable to process your payment at this time. Please check your card details or try again later.");
            } finally {
                setReservation_loading(false);
            }
        },
        [internal_ID, SERVER_URL, formData, selectedAddons]
    );

    // Memoized reservation data
    const memoizedReservation = useMemo(() => reservation, [reservation]);

    return {
        reservation_loading,
        reservation_error,
        handleFetchReservation,
        reservation: memoizedReservation,
    };
}
